import { useState, useEffect } from "react";
import { applySeo } from "@/lib/seo";
import { AgencyLogo } from "@/components/AgencyLogo";
import { BarometerNav } from "@/components/BarometerNav";

const C = {
  accent: "#6d0000",
  accentLight: "#6d000010",
  teal: "#115751",
  tealLight: "#11575110",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

const MEETERGO_URL =
  "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko";

const LEISTUNGEN = [
  {
    id: "seo",
    icon: "/manus-storage/23_1ab7147e.svg",
    title: "SEO & Sichtbarkeit",
    desc: "Online-Präsenz optimieren & gefunden werden",
  },
  {
    id: "ki",
    icon: "/manus-storage/7_8bcad2d2.svg",
    title: "KI-Sichtbarkeit",
    desc: "In ChatGPT, Gemini & Co. sichtbar werden",
  },
  {
    id: "ppc",
    icon: "/manus-storage/18_7df0ad86.svg",
    title: "PPC & Werbeanzeigen",
    desc: "Gezielte Kampagnen & maximale Reichweite",
  },
  {
    id: "beratung",
    icon: "/manus-storage/47_7e4350f7.svg",
    title: "Marketing-Beratung",
    desc: "Individuelle Expertise & Sparringspartner",
  },
  {
    id: "realitaetscheck",
    icon: "/manus-storage/32_04885248.svg",
    title: "Pro Website-RealitätsCheck",
    desc: "Status Quo Analyse & Potenziale aufdecken",
  },
  {
    id: "strategie",
    icon: "/manus-storage/icon_onpage_grundlagen_a1ddd99a.png",
    title: "Strategie & Konzept",
    desc: "Fundierte Planung & nachhaltiges Wachstum",
  },
  {
    id: "sonstiges",
    icon: "/manus-storage/46_6aebe47d.svg",
    title: "Mehr erfahren / Sonstiges",
    desc: "Für alle anderen Anfragen & Infos",
  },
];

function ProgressBar({ step }: { step: number }) {
  const steps = ["Leistung wählen", "Deine Angaben", "Termin buchen"];
  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {steps.map((label, i) => {
        const idx = i + 1;
        const isCompleted = step > idx;
        const isActive = step === idx;
        return (
          <div key={i} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-black text-sm transition-all"
                style={{
                  backgroundColor: isCompleted || isActive ? C.teal : "#e5e7eb",
                  color: isCompleted || isActive ? "#fff" : C.muted,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {isCompleted ? "✓" : idx}
              </div>
              <span
                className="text-xs mt-1.5 font-medium whitespace-nowrap"
                style={{
                  color: isActive ? C.teal : isCompleted ? C.teal : C.muted,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: isActive ? 700 : 400,
                }}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className="h-0.5 w-16 sm:w-24 mx-1 mb-5 transition-all"
                style={{ backgroundColor: step > idx ? C.teal : "#e5e7eb" }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Schritt 1: Leistung wählen ──────────────────────────────────────────────
function Step1({
  selected,
  onSelect,
  onNext,
}: {
  selected: string;
  onSelect: (id: string) => void;
  onNext: () => void;
}) {
  return (
    <div>
      <div className="text-center mb-8">
        <h1
          className="font-black uppercase leading-none mb-3"
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: "clamp(2rem, 5vw, 3rem)",
            color: C.text,
          }}
        >
          Was kann ich für dich tun?
        </h1>
        <p className="text-gray-500 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Wähle die Leistung, die dich interessiert — ich melde mich mit dem passenden Angebot.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {LEISTUNGEN.map((l) => (
          <button
            key={l.id}
            onClick={() => onSelect(l.id)}
            className="flex items-start gap-4 p-4 text-left transition-all border-2"
            style={{
              borderColor: selected === l.id ? C.teal : C.border,
              backgroundColor: selected === l.id ? C.tealLight : "#fff",
              cursor: "pointer",
            }}
          >
            <img src={l.icon} alt={l.title} className="w-7 h-7 mt-0.5 flex-shrink-0 object-contain" style={{ opacity: 0.85 }} />
            <div>
              <p
                className="font-bold text-sm"
                style={{
                  color: selected === l.id ? C.teal : C.text,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {l.title}
              </p>
              <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {l.desc}
              </p>
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={onNext}
        disabled={!selected}
        className="w-full py-4 font-black uppercase tracking-widest text-white transition-all disabled:opacity-40"
        style={{
          backgroundColor: selected ? C.teal : "#9ca3af",
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: "1.1rem",
          cursor: selected ? "pointer" : "not-allowed",
        }}
      >
        Weiter →
      </button>
      <p className="text-center text-xs text-gray-400 mt-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        Schritt 1 von 3 · Keine Verpflichtung
      </p>
    </div>
  );
}

// ── Schritt 2: Angaben ──────────────────────────────────────────────────────
interface FormData {
  name: string;
  unternehmen: string;
  email: string;
  website: string;
  problem: string;
}

function Step2({
  formData,
  onChange,
  selectedLeistung,
  onBack,
  onNext,
}: {
  formData: FormData;
  onChange: (field: keyof FormData, value: string) => void;
  selectedLeistung: string;
  onBack: () => void;
  onNext: () => void;
}) {
  const leistung = LEISTUNGEN.find((l) => l.id === selectedLeistung);
  const isValid = formData.name.trim() && formData.email.trim();

  const inputStyle = {
    border: `1px solid ${C.border}`,
    borderRadius: 0,
    padding: "12px 14px",
    fontSize: "0.875rem",
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    width: "100%",
    backgroundColor: "#fff",
  };

  return (
    <div>
      <div className="text-center mb-8">
        <h1
          className="font-black uppercase leading-none mb-3"
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: "clamp(2rem, 5vw, 3rem)",
            color: C.text,
          }}
        >
          Fast geschafft — wer bist du?
        </h1>
        <p className="text-gray-500 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Nur 4 Angaben — damit ich mich optimal vorbereiten kann.
        </p>
      </div>

      {/* Gewählte Leistung als Pill */}
      {leistung && (
        <div className="flex justify-center mb-6">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-bold"
            style={{
              backgroundColor: C.teal,
              color: "#fff",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <img src={leistung.icon} alt={leistung.title} className="w-5 h-5 flex-shrink-0 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
            <span>{leistung.title}</span>
            <button
              onClick={onBack}
              className="ml-1 opacity-70 hover:opacity-100"
              style={{ fontSize: "0.75rem" }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: C.text, fontFamily: "'DM Sans', sans-serif" }}>
              Dein Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => onChange("name", e.target.value)}
              placeholder="Max Mustermann"
              style={inputStyle}
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: C.text, fontFamily: "'DM Sans', sans-serif" }}>
              Dein Unternehmen
            </label>
            <input
              type="text"
              value={formData.unternehmen}
              onChange={(e) => onChange("unternehmen", e.target.value)}
              placeholder="Musterfirma GmbH"
              style={inputStyle}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: C.text, fontFamily: "'DM Sans', sans-serif" }}>
            Deine E-Mail-Adresse *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => onChange("email", e.target.value)}
            placeholder="max@firma.de"
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: C.text, fontFamily: "'DM Sans', sans-serif" }}>
            Deine Website (optional)
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => onChange("website", e.target.value)}
            placeholder="https://www.deinewebsite.de"
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: C.text, fontFamily: "'DM Sans', sans-serif" }}>
            Was ist dein größtes Marketing-Problem gerade?
          </label>
          <textarea
            value={formData.problem}
            onChange={(e) => onChange("problem", e.target.value)}
            placeholder="Beschreibe kurz deine aktuelle Herausforderung oder deine Ziele..."
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="px-6 py-4 font-bold uppercase tracking-widest text-sm border-2 transition-all"
          style={{
            borderColor: C.border,
            color: C.muted,
            fontFamily: "'DM Sans', sans-serif",
            backgroundColor: "transparent",
          }}
        >
          ← Zurück
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 py-4 font-black uppercase tracking-widest text-white transition-all disabled:opacity-40"
          style={{
            backgroundColor: isValid ? C.teal : "#9ca3af",
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: "1.1rem",
            cursor: isValid ? "pointer" : "not-allowed",
          }}
        >
          Weiter zum Termin →
        </button>
      </div>
      <p className="text-center text-xs text-gray-400 mt-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        Schritt 2 von 3 · Deine Daten werden nicht weitergegeben
      </p>
    </div>
  );
}

// ── Schritt 3: Termin buchen (Meetergo iFrame) ──────────────────────────────
function Step3({
  formData,
  selectedLeistung,
  onBack,
}: {
  formData: FormData;
  selectedLeistung: string;
  onBack: () => void;
}) {
  const leistung = LEISTUNGEN.find((l) => l.id === selectedLeistung);

  // Meetergo URL mit vorausgefüllten Parametern
  const redirectUrl = `${window.location.origin}/kontakt/danke`;
  const params = new URLSearchParams({
    name: formData.name,
    email: formData.email,
    ...(formData.unternehmen ? { company: formData.unternehmen } : {}),
    notes: [
      leistung ? `Leistung: ${leistung.title}` : "",
      formData.website ? `Website: ${formData.website}` : "",
      formData.problem ? `Problem: ${formData.problem}` : "",
    ]
      .filter(Boolean)
      .join(" | "),
    redirect_url: redirectUrl,
  });

  const iframeUrl = `${MEETERGO_URL}?${params.toString()}`;

  return (
    <div>
      <div className="text-center mb-6">
        <h1
          className="font-black uppercase leading-none mb-3"
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: "clamp(2rem, 5vw, 3rem)",
            color: C.text,
          }}
        >
          Wähle deinen Wunschtermin
        </h1>
        <p className="text-gray-500 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Ich schaue mir deine Situation vorher genau an und melde mich innerhalb von 24h.
        </p>
      </div>

      {/* Buchungs-Zusammenfassung */}
      <div
        className="flex flex-wrap gap-3 justify-center mb-6"
      >
        {leistung && (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold"
            style={{ backgroundColor: C.teal, color: "#fff", fontFamily: "'DM Sans', sans-serif" }}
          >
            <img src={leistung.icon} alt="" className="w-4 h-4 object-contain" style={{ filter: "brightness(0) invert(1)" }} /> {leistung.title}
          </span>
        )}
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold"
          style={{ backgroundColor: C.accentLight, color: C.accent, fontFamily: "'DM Sans', sans-serif", border: `1px solid ${C.accent}30` }}
        >
          <img src="/manus-storage/36_b604bbd9.svg" alt="" className="w-4 h-4 object-contain" style={{ opacity: 0.7 }} /> {formData.name}
        </span>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold"
          style={{ backgroundColor: C.bgGray, color: C.muted, fontFamily: "'DM Sans', sans-serif" }}
        >
          {formData.email}
        </span>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold"
          style={{ backgroundColor: "#dcfce7", color: "#166534", fontFamily: "'DM Sans', sans-serif" }}
        >
          ✓ Persönlich von Larissa
        </span>
      </div>

      {/* Meetergo iFrame */}
      <div
        className="w-full overflow-hidden mb-6"
        style={{ border: `1px solid ${C.border}`, minHeight: "600px" }}
      >
        <iframe
          src={iframeUrl}
          width="100%"
          height="650"
          frameBorder="0"
          title="Termin buchen – WERBEAGENTUR Larissa Girenko"
          allow="camera; microphone; geolocation"
          style={{ display: "block" }}
        />
      </div>

      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-bold transition-all"
        style={{ color: C.muted, fontFamily: "'DM Sans', sans-serif" }}
      >
        ← Zurück zu deinen Angaben
      </button>
      <p className="text-xs text-gray-400 mt-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        Du erhältst sofort eine Bestätigung per E-Mail
      </p>
    </div>
  );
}

// ── Haupt-Komponente ────────────────────────────────────────────────────────
export default function Kontakt() {
  useEffect(() => applySeo({
    title: "Kontakt – Jetzt Analyse anfragen | WERBEAGENTUR Larissa Girenko",
    description: "Starte deine Website-Analyse oder buche die Google Ads Potenzialanalyse. Persönliche Betreuung von Larissa Girenko – Antwort innerhalb von 24h.",
    path: "/kontakt",
  }), []);
  const [step, setStep] = useState(1);
  const [selectedLeistung, setSelectedLeistung] = useState("");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    unternehmen: "",
    email: "",
    website: "",
    problem: "",
  });

  const handleFormChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/kontakt" accentColor={C.accent} />

      {/* Hero-Streifen */}
      <div
        className="w-full py-10 px-6 text-center"
        style={{ backgroundColor: C.text }}
      >
        <p
          className="text-xs font-bold tracking-widest uppercase mb-1"
          style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}
        >
          <AgencyLogo size="sm" />
        </p>
        <h2
          className="font-black uppercase text-white"
          style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}
        >
          Analyse anfragen
        </h2>
        <p className="text-gray-400 text-sm mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Persönliche Analyse · PDF-Report · Lieferung in 72h
        </p>
      </div>

      {/* Formular-Container */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        <ProgressBar step={step} />

        {step === 1 && (
          <Step1
            selected={selectedLeistung}
            onSelect={setSelectedLeistung}
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <Step2
            formData={formData}
            onChange={handleFormChange}
            selectedLeistung={selectedLeistung}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <Step3
            formData={formData}
            selectedLeistung={selectedLeistung}
            onBack={() => setStep(2)}
          />
        )}
      </div>

      {/* Footer-Vertrauen */}
      <div
        className="border-t py-8 px-6 text-center"
        style={{ borderColor: C.border }}
      >
        <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Deine Daten werden vertraulich behandelt und nicht an Dritte weitergegeben. ·{" "}
          <a href="/datenschutz" style={{ color: C.accent }}>Datenschutzerklärung</a>
        </p>
      </div>
    </div>
  );
}
